/** @jsxImportSource @emotion/react */
import { Box, Collapse, MenuItem, Select, Tooltip, useTheme } from '@mui/material'
import {
  elementsContainer,
  elementsGrid,
  elementsList,
  elementsTitle,
  flexColumn,
  footerButtonsContainer,
  headerContainer,
  inputsTabContainer,
  inputsTabParent,
  sliderContainer,
  toggleButton,
  valueSetContainer,
  valueSetHeader,
  valueSetName,
  valueSetTitle,
} from './InputsTab.styles'
import { Button } from '@app/components/Atoms/Button'
import { FullWidthAccordion } from '@app/components/Atoms/FullWidthAccordion'
import { useEffect, useMemo, useState } from 'react'
import {
  MCAScenario,
  usePrioritisationContext,
} from '@app/contexts/ModuleContexts/PrioritisationContext'
import { PrioritySlider } from '../../PrioritySlider'
import { FancySuspense } from '@app/components/Atoms/FancySuspense'
import { convertIntegerToWords } from '@app/utils/numbers.utils'
import pluralize from 'pluralize'
import { Icon } from '@app/components/Atoms/Icon'

const sentenceCase = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}
export const InputsTab = ({
  hideContent,
  switchToResultsTab,
}: {
  hideContent?: boolean
  switchToResultsTab: () => void
}) => {
  const theme = useTheme()
  const {
    elementGroups,
    valueWeightings,
    assessment,
    scenarioGroup,
    setScenarioGroup,
    setValueWeightings,
    allScenarioGroups,
  } = usePrioritisationContext()
  const [visibleElements, setVisibleElements] = useState<{ [key: string]: boolean }>({})
  const [updatedValueWeightings, setUpdatedValueWeightings] = useState<{ [key: string]: number }>(
    {},
  )
  const [tempScenarioGroup, setTempScenarioGroup] = useState<MCAScenario | null>(null)
  const handleScenarioGroupChange = (e: string) => {
    if (!allScenarioGroups) return
    const selectedScenarioGroup = allScenarioGroups.find((group) => group.scenario_id === e)
    if (selectedScenarioGroup) {
      setTempScenarioGroup(selectedScenarioGroup)
    }
  }
  const formValuesHaveChanged = useMemo(() => {
    if (Object.keys(updatedValueWeightings).length === 0) return false
    return (
      Object.keys(valueWeightings).some(
        (key) => valueWeightings[key] !== updatedValueWeightings[key],
      ) || tempScenarioGroup?.scenario_id !== scenarioGroup?.scenario_id
    )
  }, [valueWeightings, updatedValueWeightings, tempScenarioGroup, scenarioGroup])
  const toggleVisibleElements = (name: string) => {
    setVisibleElements((prev) => ({ ...prev, [name]: !prev[name] }))
  }
  const applyChanges = () => {
    setValueWeightings(updatedValueWeightings)
    if (tempScenarioGroup) setScenarioGroup(tempScenarioGroup)
    switchToResultsTab()
  }
  useEffect(() => {
    setUpdatedValueWeightings(valueWeightings)
  }, [valueWeightings])
  useEffect(() => {
    if (!tempScenarioGroup && scenarioGroup) {
      setTempScenarioGroup(scenarioGroup)
    }
  }, [scenarioGroup, tempScenarioGroup])

  const scenariosByHazard = useMemo(() => {
    const hazards = tempScenarioGroup?.hazard_scenarios.reduce(
      (acc, scenario) => {
        const hazardName = scenario.display_name
        if (!acc[hazardName]) {
          acc[hazardName] = []
        }
        acc[hazardName].push(scenario.scenario_name)
        return acc
      },
      {} as { [key: string]: string[] },
    )
    return hazards
  }, [tempScenarioGroup])

  return (
    <Box css={inputsTabParent()}>
      <Box css={inputsTabContainer()}>
        <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, overflowY: 'auto' }}>
          <FullWidthAccordion
            level="h2"
            defaultExpanded={true}
            body={
              <Box css={[flexColumn, { opacity: hideContent ? 0 : 1 }]}>
                <Box css={headerContainer}>
                  <h4>
                    TOTAL OF {convertIntegerToWords(elementGroups.length).toUpperCase()} VALUE{' '}
                    {pluralize('SET', elementGroups.length)}
                  </h4>
                  <Box className="buttons">
                    <Button
                      variant="text"
                      onClick={() => {
                        setUpdatedValueWeightings({
                          ...valueWeightings,
                          ...assessment?.value_set_config.value_set_weights,
                        })
                      }}
                    >
                      Reset
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => {
                        setUpdatedValueWeightings((prev) => {
                          return Object.keys(prev).reduce(
                            (acc, key) => {
                              acc[key] = 3
                              return acc
                            },
                            {} as { [key: string]: number },
                          )
                        })
                      }}
                    >
                      Balance
                    </Button>
                  </Box>
                </Box>
                <Box>
                  <p css={{ padding: '0 8px' }}>
                    Use the sliders to adjust the importance of each value set below.
                  </p>
                  {elementGroups.map(({ value_set_id, elements }) => {
                    return (
                      <Box key={value_set_id} css={valueSetContainer}>
                        <Box css={valueSetHeader(theme)}>
                          <Box css={valueSetTitle}>
                            <b css={valueSetName}>{sentenceCase(value_set_id)}</b>
                          </Box>
                          <Button
                            variant="text"
                            css={toggleButton(theme)}
                            onClick={() => toggleVisibleElements(value_set_id)}
                          >
                            {visibleElements[value_set_id] ? 'Hide' : 'View'} elements
                          </Button>
                        </Box>
                        <Box css={sliderContainer}>
                          <PrioritySlider
                            value={updatedValueWeightings[value_set_id] ?? 1}
                            onChange={(_, value) => {
                              setUpdatedValueWeightings({
                                ...updatedValueWeightings,
                                [value_set_id]: value as number,
                              })
                            }}
                            color="uintel"
                          />
                        </Box>
                        <Collapse in={visibleElements[value_set_id]}>
                          <Box css={elementsContainer(theme)}>
                            <h5 css={elementsTitle}>ELEMENTS</h5>
                            <Box css={elementsGrid}>
                              <ul css={elementsList}>
                                {elements
                                  .slice(0, Math.ceil(elements.length / 2))
                                  .map((element) => (
                                    <li key={element.type}>{element.label}</li>
                                  ))}
                              </ul>
                              <ul css={elementsList}>
                                {elements.slice(Math.ceil(elements.length / 2)).map((element) => (
                                  <li key={element.type}>{element.label}</li>
                                ))}
                              </ul>
                            </Box>
                          </Box>
                        </Collapse>
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            }
            title="Values"
          />
          <FullWidthAccordion
            level="h2"
            defaultExpanded={true}
            body={
              <Box sx={{ paddingBottom: '16px', opacity: hideContent ? 0 : 1 }}>
                <Box sx={{ padding: '0 8px' }}>
                  <Box sx={{ paddingTop: 4, fontSize: '14px' }}>Select a scenario below</Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px 0',
                      gap: 8,
                    }}
                  >
                    <FancySuspense isLoading={!scenarioGroup}>
                      {scenarioGroup && allScenarioGroups && (
                        <>
                          <Box sx={{ flexGrow: 1 }}>
                            {tempScenarioGroup && (
                              <Select
                                css={{ width: '100%' }}
                                value={tempScenarioGroup.scenario_id}
                                onChange={(e) => {
                                  handleScenarioGroupChange(e.target.value as string)
                                }}
                              >
                                {allScenarioGroups.map((sg) => (
                                  <MenuItem key={sg.scenario_id} value={sg.scenario_id}>
                                    {sg.scenario_id}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          </Box>
                          <Box
                            sx={{
                              backgroundColor: '#f5f5f5',
                              padding: '8px 24px 16px 12px',
                              fontSize: '14px',
                              maxWidth: '50%',
                            }}
                          >
                            <b>Hazards in this scenario</b>
                            <ul css={{ marginLeft: '24px' }}>
                              {scenariosByHazard
                                ? Object.entries(scenariosByHazard)
                                    .sort((a, b) => a[0].localeCompare(b[0]))
                                    .map((hazardWithScenarios) => (
                                      <li
                                        key={hazardWithScenarios[0]}
                                        css={{
                                          lineHeight: '1.3',
                                          paddingTop: '4px',
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'inline-flex',
                                            gap: '8px',
                                          }}
                                        >
                                          {hazardWithScenarios[0]}
                                          <Tooltip
                                            title={
                                              <span>
                                                Includes scenarios:
                                                <br />
                                                {hazardWithScenarios[1].map((scenario, index) => (
                                                  <span key={index}>
                                                    - {scenario}
                                                    <br />
                                                  </span>
                                                ))}
                                              </span>
                                            }
                                            placement="right"
                                            arrow
                                          >
                                            <Box>
                                              <Icon
                                                iconName="Info"
                                                colour={theme.palette.action.active}
                                              />
                                            </Box>
                                          </Tooltip>
                                        </Box>
                                      </li>
                                    ))
                                : null}
                            </ul>
                          </Box>
                        </>
                      )}
                    </FancySuspense>
                  </Box>
                </Box>
              </Box>
            }
            title="Scenarios"
          />
        </Box>
      </Box>
      <Box css={footerButtonsContainer(theme)}>
        <Button variant="contained" onClick={applyChanges} disabled={!formValuesHaveChanged}>
          Apply changes
        </Button>
      </Box>
    </Box>
  )
}
